import BrandLogo from "@/components/BrandLogo";
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import API, { setAccessToken } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError("No invitation token in link."); setLoading(false); return; }
    API.get(`/providers/invite-info?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setInviteInfo(data))
      .catch(err => setError(err?.response?.data?.detail || "Invitation not valid."))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      const { data } = await API.post("/providers/accept-invite", { token, password });
      if (data?.access_token) {
        setAccessToken(data.access_token);
      }
      await checkAuth();
      navigate("/provider", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't accept the invitation.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-primary/10" data-testid="accept-invite-page">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <BrandLogo className="w-56 max-w-full mx-auto mb-4" testId="accept-invite-logo" />
          <h1 className="text-3xl font-display font-bold">Accept Invitation</h1>
          <p className="text-sm text-muted-foreground mt-1">Elevated Psychiatry — provider portal</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : error && !inviteInfo ? (
            <div className="text-center space-y-3">
              <AlertCircle className="w-8 h-8 text-red-600 mx-auto" />
              <p className="text-sm text-red-700">{error}</p>
              <Link to="/login" className="text-sm text-primary hover:underline inline-block">Go to patient sign in →</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="accept-invite-form">
              <div className="bg-accent rounded-xl p-4 text-sm">
                You've been invited as a provider for <strong>{inviteInfo?.email}</strong>
                {inviteInfo?.name ? ` (${inviteInfo.name})` : ""}. Set a password to activate your account.
              </div>
              <div>
                <Label className="text-sm font-medium">Password</Label>
                <Input data-testid="accept-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" className="mt-1.5 h-12" minLength={8} required />
              </div>
              <div>
                <Label className="text-sm font-medium">Confirm Password</Label>
                <Input data-testid="accept-confirm-input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="mt-1.5 h-12" minLength={8} required />
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              <Button data-testid="accept-invite-submit" type="submit" className="w-full h-12 rounded-full text-base gap-2" disabled={submitting}>
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Activate my provider account</>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
